import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { Currency, Site } from '@debbie/common/dist/index.js';
import { getBillingSums } from '@debbie/common/dist/economy/billing/organize.js';
import { objectBy } from '@debbie/common/dist/util/index.js';
import { c, h } from '@debbie/cortex/dist/index.js';
import { billingsEntity, useBilling, useBillings, } from '@debbie/cortex/dist/billings/index.js';
import * as billingsAPI from '@debbie/cortex/dist/api/billing.js';
import THEME from '../../theme.js';
import Badge from '../Badge.js';
import BillingPreview from '../BillingPreview/BillingPreview.js';
import { ExternalLink, FilePlus, MoreVertical, Send } from '../Icons/index.js';
import Modal from '../Modal.js';
import Money from '../Money.js';
import PlaceholderLoader from '../PlaceholderLoader.js';
import PowerTable, { FilterType } from '../PowerTable.js';
import Timestamp from '../Timestamp.js';
import { powerTableValues } from './billing-table-utils.js';
import ButtonDrop from '../ButtonDrop.js';
import Button from '../Button.js';
import ButtonGroup from '../ButtonGroup.js';
import q from '@debbie/cortex/dist/query.js';
import keys from '@debbie/common/dist/cache/keys.js';
import { ExclusiveContentBadge } from '../ExclusiveContent.js';
const Billings = (props) => {
    const lang = useSelector((s) => s.lang);
    const site = useSelector((s) => s.site);
    const billingsLoadable = useBillings({
        creditorId: props.creditorId,
        caseId: props.caseId,
        depositCaseVoucherId: props.depositCaseVoucherId,
        billingCollectionId: props.billingCollectionId,
        currency: props.currency,
        billingAppendixId: props.billingAppendixId,
    });
    const [previewBillingId, setPreviewBillingId] = useState(null);
    const [optionsOpen, setOptionsOpen] = useState(false);
    const [deletingBillingId, setDeletingBillingId] = useState(null);
    const billings = h.hasData(billingsLoadable)
        ? billingsLoadable.data
        : null;
    const handleCategories = billings?.reduce((acc, billing) => {
        if (!acc[billing.handle]) {
            acc[billing.handle] = {
                label: billing.handle,
                type: billing.handle,
            };
        }
        return acc;
    }, {});
    return (React.createElement("div", null,
        billings ? (React.createElement("div", { style: { fontSize: '0.8em' } },
            React.createElement(PowerTable, { data: billings.map((billing) => ({
                    id: billing.id,
                    billingId: billing.billingId,
                    date: billing.date,
                    handle: billing.handle,
                    timingType: billing.timingType,
                    creditorId: billing.creditorId,
                    creditorName: billing.creditorName,
                    caseSequentialIds: billing.caseSequentialIds,
                    billingEmailed: billing.billingEmailed,
                    addedToPayouts: billing.addedToPayouts,
                    revenue: billing.revenue + billing.revenueCollectionCommission,
                    payout: billing.payout,
                    vat: billing.vat,
                    disbursements: billing.disbursements,
                    overpayments: billing.overpayments,
                    balance: getBillingSums(billing).balance,
                    currency: billing.currency,
                    options: billing.id,
                })), genKey: (billing) => billing.billingId, defaultSortKey: "date", defaultSortDirection: "DESC", onClick: (billing) => {
                    setPreviewBillingId(billing.billingId);
                }, pagination: {
                    type: 'pages',
                    pageSize: 25,
                }, 
                // @ts-ignore
                values: site === Site.Client
                    ? {
                        date: {
                            label: lang.billing.date,
                            map: (date) => React.createElement(Timestamp, { date: date }),
                            filter: {
                                type: FilterType.DateRange,
                            },
                        },
                        id: {
                            label: lang.billing.billingSequentialId,
                            map: (id) => `B${id}`,
                        },
                        handle: {
                            label: lang.billing.billingsAppendix,
                            toCmp: (handle) => handle,
                            map: (handle) => handle,
                            filter: {
                                type: FilterType.Category,
                                categories: handleCategories,
                            },
                        },
                        caseSequentialIds: {
                            label: lang.billing.billingsCases,
                            toCmp: (caseSequentialIds) => caseSequentialIds.length,
                            map: (caseSequentialIds) => (React.createElement(React.Fragment, null,
                                caseSequentialIds.length,
                                ' ',
                                React.createElement(ExternalLink, { style: { cursor: 'pointer' }, onClick: () => {
                                        c.redirect.redirect(`/cases/${caseSequentialIds.join(',')}`);
                                    } }))),
                        },
                        revenue: {
                            label: lang.billing.fees,
                            align: 'right',
                            toCmp: (revenue) => revenue,
                            map: (revenue) => React.createElement(Money, { amount: revenue }),
                            filter: {
                                type: FilterType.MoneyRange,
                            },
                        },
                        vat: {
                            label: lang.billing.vat,
                            align: 'right',
                            toCmp: (vat) => vat,
                            map: (vat) => React.createElement(Money, { amount: vat }),
                            filter: {
                                type: FilterType.MoneyRange,
                            },
                        },
                        disbursements: {
                            label: lang.billing.disbursements,
                            align: 'right',
                            toCmp: (disbursements) => disbursements,
                            map: (disbursements) => React.createElement(Money, { amount: disbursements }),
                            filter: {
                                type: FilterType.MoneyRange,
                            },
                        },
                        payout: {
                            label: lang.billing.payout,
                            align: 'right',
                            toCmp: (payout) => payout,
                            map: (payout) => React.createElement(Money, { amount: payout }),
                            filter: {
                                type: FilterType.MoneyRange,
                            },
                        },
                        balance: {
                            label: lang.billing.balance,
                            align: 'right',
                            toCmp: (balance) => balance,
                            map: (balance) => (React.createElement("b", null,
                                React.createElement(Money, { amount: balance }))),
                            filter: {
                                type: FilterType.MoneyRange,
                            },
                        },
                        currency: {
                            label: lang.billing.billingsCurrency,
                            align: 'right',
                            toCmp: (currency) => currency,
                            map: (currency) => (React.createElement(Badge, { theme: THEME.COLOR_ACCENT }, currency)),
                            filter: {
                                type: FilterType.Category,
                                categories: objectBy(Object.values(Currency).map((currency) => ({
                                    type: currency,
                                    label: currency,
                                })), (c) => c.type),
                            },
                        },
                    }
                    : {
                        date: {
                            label: lang.billing.date,
                            map: (date) => React.createElement(Timestamp, { date: date }),
                            filter: {
                                type: FilterType.DateRange,
                            },
                        },
                        id: {
                            label: lang.billing.billingSequentialId,
                            map: (id) => `B${id}`,
                        },
                        addedToPayouts: {
                            label: '',
                            toCmp: (addedToPayouts) => addedToPayouts,
                            map: (addedToPayouts, row) => row.balance < 0 ? (React.createElement(FilePlus, { style: {
                                    color: addedToPayouts
                                        ? THEME.COLOR_ACCENT
                                        : undefined,
                                } })) : null,
                        },
                        billingEmailed: {
                            label: '',
                            toCmp: (billingEmailed) => billingEmailed,
                            map: (billingEmailed) => (React.createElement(Send, { style: {
                                    color: billingEmailed
                                        ? THEME.COLOR_ACCENT
                                        : undefined,
                                    height: '1em',
                                    width: '1em',
                                } })),
                        },
                        ...powerTableValues({
                            lang,
                            // @ts-ignore
                            site,
                            // @ts-ignore
                            handleCategories,
                        }),
                        options: {
                            label: '',
                            map: (id) => props.deleteable ? (React.createElement(ButtonDrop, { open: optionsOpen, onOpen: () => setOptionsOpen(true), onClose: () => setOptionsOpen(false), size: "mini", dropDownPosition: "left", customButton: React.createElement(MoreVertical, { style: {
                                        width: '16px',
                                        height: '16px',
                                    } }) },
                                React.createElement("div", { style: {
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '5px',
                                    }, onClick: (e) => e.stopPropagation() },
                                    React.createElement(Button, { size: "small", theme: "dangerous", onClick: () => {
                                            setDeletingBillingId(id);
                                        } },
                                        lang.misc.delete,
                                        " (exclusive content)")))) : (React.createElement("div", null)),
                        },
                    } }))) : (React.createElement(PlaceholderLoader, null)),
        previewBillingId && (React.createElement(Modal, { onToggle: () => setPreviewBillingId(null), maxWidth: "1600px", header: lang.billing.appendix },
            React.createElement(PreviewBilling, { billingId: previewBillingId, closeModal: () => setPreviewBillingId(null), highlightCaseId: props.highlightCaseId }))),
        deletingBillingId && (React.createElement(Modal, { onToggle: () => setDeletingBillingId(null), header: lang.misc.delete, maxWidth: "500px" },
            React.createElement(ExclusiveContentBadge, null),
            React.createElement("div", null, lang.billing.deletionWarning),
            React.createElement(ButtonGroup, null,
                React.createElement(Button, { theme: "dangerous", onClick: async () => {
                        try {
                            await billingsAPI.deleteById(deletingBillingId.toString());
                            q.invalidate(keys.write.modify(billingsEntity, deletingBillingId.toString()));
                            setDeletingBillingId(null);
                        }
                        catch (e) {
                            console.error('Error deleting billing:', e);
                            alert('Something went wrong when deleting the billing.\n');
                        }
                    } }, lang.misc.delete),
                React.createElement(Button, { onClick: () => {
                        setDeletingBillingId(null);
                    } }, lang.misc.cancel))))));
};
export default Billings;
const PreviewBilling = (props) => {
    const billingLoadable = useBilling(props.billingId);
    const billing = h.hasData(billingLoadable) ? billingLoadable.data : null;
    return billing ? (React.createElement(BillingPreview, { billing: billing, closeModal: props.closeModal, highlightCaseId: props.highlightCaseId })) : (React.createElement(PlaceholderLoader, null));
};
//# sourceMappingURL=Billings.js.map